/**
 * Veltrona — header behaviour: sticky modes and dropdown menus.
 */
(() => {
  'use strict';

  class VtHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky;
      this.section = this.closest('.shopify-section') || this;
      this.lastY = window.scrollY;
      this.updateOffset();
      this.resizeObserver = new ResizeObserver(() => this.updateOffset());
      this.resizeObserver.observe(this);
      if (this.hasAttribute('data-transparent')) {
        this.addEventListener('pointerenter', () => this.classList.add('is-hovered'));
        this.addEventListener('pointerleave', () => this.classList.remove('is-hovered'));
        this.addEventListener('focusin', () => this.classList.add('is-hovered'));
        this.addEventListener('focusout', (e) => !this.contains(e.relatedTarget) && this.classList.remove('is-hovered'));
      }
      if (this.mode === 'none') {
        if (this.hasAttribute('data-transparent')) {
          window.addEventListener('scroll', () => this.classList.toggle('is-solid', window.scrollY > 10), { passive: true });
        }
        return;
      }
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    updateOffset() {
      document.documentElement.style.setProperty('--header-offset', `${this.offsetHeight}px`);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        const y = window.scrollY;
        const top = this.section.offsetTop;
        const stuck = y > top + 4;
        this.classList.toggle('is-stuck', stuck);
        if (this.hasAttribute('data-transparent')) this.classList.toggle('is-solid', stuck);
        if (this.mode === 'scroll-up') {
          const goingDown = y > this.lastY;
          const past = y > top + this.offsetHeight * 2;
          const menuOpen = this.querySelector('.dropdown.is-open');
          this.classList.toggle('is-hidden', goingDown && past && !menuOpen);
        }
        this.lastY = y;
      });
    }
  }

  class VtDropdown extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || this.dataset.ready) return;
      this.dataset.ready = 'true';

      this.summary.addEventListener('click', (e) => {
        e.preventDefault();
        this.isOpen ? this.close() : this.open();
      });

      const hoverable = window.matchMedia('(hover: hover)');
      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !hoverable.matches) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.open(), 60);
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.close(), 180);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
          this.summary.focus();
        }
      });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.close();
      });
      document.addEventListener('click', (e) => {
        if (this.isOpen && !this.contains(e.target)) this.close();
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    /** Stretches a mega menu to the full header width, below the header. */
    placeMega() {
      const mega = this.querySelector('.mega');
      const wrapper = this.closest('.header-wrapper');
      if (!mega || !wrapper) return;
      mega.style.left = '0px';
      mega.style.top = '0px';
      const anchor = (mega.offsetParent || wrapper).getBoundingClientRect();
      const w = wrapper.getBoundingClientRect();
      mega.style.left = `${w.left - anchor.left}px`;
      mega.style.width = `${w.width}px`;
      mega.style.top = `${w.bottom - anchor.top}px`;
    }

    open() {
      document.querySelectorAll('vt-dropdown.is-open').forEach((d) => d !== this && d.close());
      this.details.open = true;
      this.placeMega();
      this.summary.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.classList.add('is-open'));
    }

    close() {
      if (!this.isOpen && !this.details.open) return;
      this.classList.remove('is-open');
      this.summary.setAttribute('aria-expanded', 'false');
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.isOpen) this.details.open = false;
      }, 350);
    }
  }

  /** Sliding pill that follows the hovered top-level menu item. */
  const initIndicator = (list) => {
    if (list.dataset.indicatorReady) return;
    list.dataset.indicatorReady = 'true';
    const pill = list.querySelector('.header-nav__indicator');
    if (!pill) return;
    const moveTo = (link) => {
      if (!link) {
        pill.classList.remove('is-visible');
        return;
      }
      const l = list.getBoundingClientRect();
      const r = link.getBoundingClientRect();
      pill.style.setProperty('--x', `${r.left - l.left}px`);
      pill.style.setProperty('--w', `${r.width}px`);
      pill.classList.add('is-visible');
    };
    list.querySelectorAll('.header-nav__item').forEach((item) => {
      const link = item.querySelector('.header-nav__link');
      item.addEventListener('pointerenter', () => moveTo(link));
      item.addEventListener('focusin', () => moveTo(link));
    });
    list.addEventListener('pointerleave', () => moveTo(null));
    list.addEventListener('focusout', (e) => {
      if (!list.contains(e.relatedTarget)) moveTo(null);
    });
  };

  const initNav = (scope = document) => scope.querySelectorAll('[data-nav-indicator]').forEach(initIndicator);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initNav());
  else initNav();
  document.addEventListener('shopify:section:load', (e) => initNav(e.target));

  if (!customElements.get('vt-header')) customElements.define('vt-header', VtHeader);
  if (!customElements.get('vt-dropdown')) customElements.define('vt-dropdown', VtDropdown);
})();
